import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartDesignSpace,
  FlowchartForValidation,
  GraphBuilderEdge,
  GraphBuilderEdgeTemplate,
  GraphBuilderNode,
  GraphBuilderNodeTemplate,
} from 'processes/graph-builder/validation/GraphBuilderConstants';
import {
  ProcessFlowchartEndNodeDetails,
  ProcessFlowchartOutputNodeDetails,
  ProcessFlowchartRootNodeDetails,
  ProcessFlowchartRouterNodeDetails,
  ProcessFlowchartTaskSchemaNodeDetails,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartNodeDetailType';

import {ProcessFlowchartSchemaMapperConfigSpec} from '../../dao/ProcessSchemaDAO';

export interface SchemaMapperConfig {
  clientId: string;
  fromTaskSchemaIds: Array<string>;
  toTaskSchemaId: string;
}

export interface ProcessGraphBuilderNodeTemplate extends GraphBuilderNodeTemplate {
  name: string;
  schemaId: string;
  inboundMapperConfigs: Array<SchemaMapperConfig>;
}

export const enum ProcessGraphBuilderNodeType {
  FLOWCHART_TASK = 'FLOWCHART_TASK',
  FLOWCHART_STEP = 'FLOWCHART_STEP',
  FLOWCHART_OUTPUT = 'FLOWCHART_OUTPUT',
}

interface ProcessGraphBuilderBaseNodeDetails {
  name: string;
  nodeType: ProcessGraphBuilderNodeType;
}

export type ProcessGraphBuilderRootNode = GraphBuilderNode &
  ProcessFlowchartRootNodeDetails &
  ProcessGraphBuilderBaseNodeDetails;

export type ProcessGraphBuilderTaskNode = GraphBuilderNode &
  ProcessFlowchartTaskSchemaNodeDetails &
  ProcessGraphBuilderBaseNodeDetails;

export type ProcessGraphBuilderRouterNode = GraphBuilderNode &
  ProcessFlowchartRouterNodeDetails &
  ProcessGraphBuilderBaseNodeDetails;

export type ProcessGraphBuilderOutputNode = GraphBuilderNode &
  ProcessFlowchartOutputNodeDetails &
  ProcessGraphBuilderBaseNodeDetails;

export type ProcessGraphBuilderEndNode = GraphBuilderNode &
  ProcessFlowchartEndNodeDetails &
  ProcessGraphBuilderBaseNodeDetails;

export type ProcessGraphBuilderNode =
  | ProcessGraphBuilderRootNode
  | ProcessGraphBuilderTaskNode
  | ProcessGraphBuilderOutputNode
  | ProcessGraphBuilderRouterNode
  | ProcessGraphBuilderEndNode;

export type ProcessGraphBuilderEdgeTemplate = GraphBuilderEdgeTemplate;

export type ProcessGraphBuilderEdge = GraphBuilderEdge;

export interface ProcessTaskSchemaForGraphBuilderValidation {
  __typename: string;
  id: string;
  name: string;
  clientOrigin: ProcessClientOrigin;
  outputSchema: {id: string} | null;
}

export interface SchemaMapperConfigWithMapperData {
  clientId: string;
  fromTaskSchemas: Array<{id: string; name: string}>;
  toTaskSchema: {id: string; name: string};
  mapperConfig: ProcessFlowchartSchemaMapperConfigSpec;
}

export interface ProcessFlowchartConfigForGraphBuilder {
  availableTaskSchemas: Array<{id: string; name: string}>;
  schemaMapperConfigs: Array<SchemaMapperConfigWithMapperData>;
}

export interface ProcessFlowchartDesignSpace extends FlowchartDesignSpace<ProcessGraphBuilderNodeTemplate> {
  availableNodes: Array<ProcessGraphBuilderNodeTemplate>;
  availableEdges: Array<ProcessGraphBuilderEdgeTemplate>;
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation;
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder;
}

// Sub-type for a Workflow flowchart, with more data
export interface ProcessFlowchartForValidation extends FlowchartForValidation {
  nodes: Array<ProcessGraphBuilderNode>;
  edges: Array<ProcessGraphBuilderEdge>;
}
